import "../../assets/css/tour/tour-child-profile.css";
import { useContext, useEffect, useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCakeCandles } from "@fortawesome/free-solid-svg-icons";
import RCDSStateContext from "../../rcds/RCDSStateContext";
import MainStateContext from "../../state/mainStateContext";
import AlertMessage from "../Shared/AlertMessage";
import Shimmer from "../Shared/Shimmer";
import {
  removeParamsFromURL,
  addQueryParam,
  parseDateParts,
  monthNames,
  integerToOrdinal,
  getStoredChildData,
  setStoredChildData,
  removeStoredChildData,
  getCheckoutProduct,
  getQueryParam
} from "../../helpers";
import { addContribution } from "../../services/addContribution";
import { getRequestKey } from "../../services/ajaxRequestKey";
import appSettings from "../../settings/appSettings";
import { GoalRelatedData, Product } from "../../types";
const autoChildLookup = !!getQueryParam("childid");
const { tourData, jumbotronEnabled = false } = appSettings;

type TourChildProfileProps = {
  randomChildLookupKeys: string[];
  addProductsToState: (products: Product[]) => void;
};

export function TourChildProfile(props: TourChildProfileProps) {
  const { randomChildLookupKeys, addProductsToState } = props;
  const mainContext = useContext(MainStateContext);
  const rcdsContext = useContext(RCDSStateContext);
  const [isReady, setIsReady] = useState(false);
  const childDataStatus = rcdsContext?.state?.status || "loading";
  const childData = rcdsContext?.state?.childData || {};
  // const childNotFoundList = rcdsContext?.state?.childNotFoundList || [];
  const childLookupError = rcdsContext?.state?.lookupError || "";
  // const overlayActive = mainContext?.state?.appOverlayActive || false;
  const { identifier, givenName, age, gender, countryName, countryCode, cgpUrl, birthdate } =
    childData;
  const boyGirl = gender?.toLowerCase() === "male" ? "boy" : "girl";
  const birthdateVal = new Date(birthdate).getTime();
  const birthMonthIndex = new Date(birthdate).getMonth();
  const [birthYear, birthMonth, birthDay] = parseDateParts(birthdate);
  const birthDayOrdinal = integerToOrdinal(birthDay);
  const ageText = age ? `${age}-year-old` : "baby";

  const findChild = useCallback(() => {
    removeParamsFromURL("view");
    mainContext.toggleOverlay(true);
    rcdsContext.getChildData();
  }, [rcdsContext]);

  const beginCheckoutContribution = async (goalRef: string, relatedData: GoalRelatedData) => {
    // Get Request Key
    const requestKey = await getRequestKey()
      .then((requestKey: string) => requestKey)
      .catch(() => null);

    // Add Contribution
    addContribution(goalRef, relatedData, requestKey);
  };

  const handleStoredChildData = (childData) => {
    const storedChildData = getStoredChildData();
    const isNewData = !storedChildData || storedChildData?.identifier !== childData?.identifier;

    if (isNewData) {
      setStoredChildData(childData);
    }

    return {
      isNewChild: isNewData,
      storedChildData
    };
  };

  useEffect(() => {
    if (autoChildLookup) {
      mainContext.toggleOverlay(true);
    }
  }, []);

  useEffect(() => {
    if (autoChildLookup && childDataStatus === "pending") {
      const childId = getQueryParam("childid");

      if (randomChildLookupKeys.includes(childId)) {
        rcdsContext.getChildData("");
      } else {
        removeStoredChildData();
        rcdsContext.getChildData(childId);
      }
    }
  }, [childDataStatus]);

  useEffect(() => {
    if (!["pending", "loading"].includes(childDataStatus)) {
      mainContext.toggleOverlay(false);
    }

    if (childDataStatus === "success") {
      // Update to current childid
      addQueryParam("childid", childData.identifier);

      const goalRef = tourData?.currentGoalRef?.goal_ref;

      const { isNewChild } = handleStoredChildData(childData);

      // Add Contribution when new child has been added  - don't repeat beginCheckoutContribution if the child has already been added
      if (isNewChild && goalRef && jumbotronEnabled) {
        beginCheckoutContribution(goalRef, {
          actionType: "begin_checkout",
          timestamp: new Date().toISOString(),
          childData: {
            photoUrl: childData.cgpUrl,
            thumbUrl: childData.cgpThumbnailUrl,
            name: childData.givenName,
            guid: childData.identifier,
            gender: childData.gender
          }
        });
      }

      const goalTrackerInfo =
        (goalRef && {
          goalRef: tourData?.currentGoalRef?.goal_ref || "",
          ...(tourData?.currentGoalRef?.goal_ref
            ? { goalRef: tourData?.currentGoalRef?.goal_ref }
            : {}),
          relatedData:
            (jumbotronEnabled && {
              actionType: "purchase",
              childData: {
                photoUrl: childData.cgpUrl,
                thumbUrl: childData.cgpThumbnailUrl,
                name: childData.givenName,
                guid: childData.identifier,
                gender: childData.gender
              }
            }) ||
            null
        }) ||
        null;

      // Get Product
      const sponsorshipProduct = getCheckoutProduct({
        productType: "sponsorship",
        goalTrackerInfo: goalTrackerInfo,
        sponsorshipInfo: {
          identifier: childData.identifier,
          countryName: childData.countryName,
          projectName: childData.projectName,
          photoUrl: childData.cgpUrl,
          childName: childData.givenName
        }
      });

      // Trigger Checkout Ready
      setTimeout(() => {
        if (!isReady) {
          window.dispatchEvent(new CustomEvent("app:checkout:ready"));
          setIsReady(true);
        }
      }, 850);

      // Add Product to Checkout
      addProductsToState([sponsorshipProduct]);
    }
  }, [childDataStatus, childData, isReady]);

  useEffect(() => {
    if (
      !autoChildLookup &&
      !isReady &&
      !Object.keys(childData).length &&
      childDataStatus === "pending"
    ) {
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("app:checkout:ready"));
        setIsReady(true);
      });
    }
  }, [autoChildLookup, childData, childDataStatus, isReady]);

  const hasAllChildVars = [identifier, givenName, cgpUrl].every(Boolean);
  const childVarsStyles = hasAllChildVars ? {} : { opacity: 0 };

  if (!autoChildLookup && !Object.keys(childData).length && childDataStatus === "pending") {
    // TODO: Abstract this into a separate component
    return (
      <div className={`TourChildProfile wanttosponsor`}>
        <p>Want to sponsor a child?</p>
        <button
          className={`btn btn-primary`}
          onClick={findChild}
          data-gtm="true"
          data-link-target="random_child"
          data-link-info="sponsor_now"
          data-link-type="button">
          Sponsor Now
        </button>
      </div>
    );
  } else if (["loading", "pending"].includes(childDataStatus)) {
    return (
      <div className={`TourChildProfile`}>
        <div className="card__wrapper child-tile child-index-0">
          <Shimmer isLoading={true} width="282px" height="376px" />
          <div className="child-info card__body">
            <Shimmer isLoading={true} width="100%" height="34px" />
            <Shimmer isLoading={true} width="100%" height="49px" />
            <Shimmer isLoading={true} width="100%" height="19px" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`TourChildProfile`}>
      <AlertMessage visible={!!childLookupError} alertType="warning" className="child-info__alert">
        {childLookupError}
      </AlertMessage>

      <div
        className="card__wrapper child-tile child-index-0 "
        data-child-identifier={identifier}
        style={childVarsStyles}>
        <div className="child-photo card__media zoom-hover--card zoom-hover__outer" tabIndex={0}>
          <img
            className="zoom-hover__inner card__media--img child-photo-thumb"
            src={cgpUrl}
            alt={`Photo of ${givenName}`}
            width="480"
            height="640"
          />
        </div>
        <div className="child-info card__body">
          <p className="child-name card__title epsilon" tabIndex={0}>
            {givenName}
          </p>
          <p className="child-info__text">
            <span className="child-name">{givenName}</span> is a{" "}
            <span className="child-age-number">{ageText}</span>{" "}
            <span className="child-gender">{boyGirl}</span> from{" "}
            <span className="child-country" data-iso-code={countryCode}>
              {countryName}
            </span>{" "}
            created to shine.
          </p>
          <p className="child-birthday centi">
            <span className="cake">
              <FontAwesomeIcon icon={faCakeCandles} className="birthday-cake" /> Birthdate: {"  "}
            </span>
            <span
              className="child-birth-date"
              data-child-birth-date={`${birthYear}-${birthMonth}-${birthDay}`}>
              <time className="ChildBirthDate" dateTime={`${birthdateVal}`}>
                {monthNames.short[birthMonthIndex]} {birthDayOrdinal}, {birthYear}
              </time>
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default TourChildProfile;
